import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config';
import { isLiquorModuleEnabled } from '../../config/featureFlags';
import { apiFetch } from '../../utils/api';
import {
  FacilitySpiritContainer,
  filterFacilitySpiritContainers,
  formatSpiritProofGallons,
  groupSpiritContainersByKind,
  isFacilitySpiritsSite,
  isSpiritContainerDestroyed,
  spiritContainerFillPercent,
  spiritsDeepLink,
} from '../../utils/facilitySpirits';

type Props = {
  siteId: string;
  /** Bump to reload after an external action (unused today; reserved). */
  reloadKey?: number;
};

/** Pause before the single automatic retry of the containers fetch. */
export const SPIRITS_RETRY_DELAY_MS = 400;

function prefersReducedMotion(): boolean {
  try {
    return Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches);
  } catch {
    return false;
  }
}

/**
 * Distillery Facility View overlay: spirits drums/barrels as aging-barrel-style tiles.
 * Does not touch brewery equipment, drag transfers, or QuickBooks.
 */
const FacilitySpiritsPanel: React.FC<Props> = ({ siteId, reloadKey = 0 }) => {
  const navigate = useNavigate();
  const enabled = isLiquorModuleEnabled() && isFacilitySpiritsSite(siteId);
  const [containers, setContainers] = useState<FacilitySpiritContainer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showEmpty, setShowEmpty] = useState(false);
  const [selected, setSelected] = useState<FacilitySpiritContainer | null>(null);
  const [retryKey, setRetryKey] = useState(0);
  const actionsRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setSelected(null);
  }, [siteId]);

  useEffect(() => {
    if (!enabled || !siteId) {
      setContainers([]);
      setError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    // Hard refresh on tablets: the browser revalidates this GET (If-None-Match → 304)
    // and can hand fetch an unusable body, leaving the panel blank until the user
    // navigates away and back. Always ask for a fresh copy and retry once.
    const fetchContainers = async (): Promise<FacilitySpiritContainer[]> => {
      const res = await apiFetch(
        `${API_BASE_URL}/api/liquor/containers?siteId=${encodeURIComponent(siteId)}`,
        { cache: 'no-store' }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      if (!Array.isArray(data)) throw new Error('Unexpected containers response');
      return data;
    };
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        let data: FacilitySpiritContainer[];
        try {
          data = await fetchContainers();
        } catch {
          if (cancelled) return;
          await new Promise((resolve) => setTimeout(resolve, SPIRITS_RETRY_DELAY_MS));
          if (cancelled) return;
          data = await fetchContainers();
        }
        if (!cancelled) setContainers(data);
      } catch (err: unknown) {
        if (!cancelled) {
          setContainers([]);
          setError(err instanceof Error ? err.message : 'Could not load spirits containers');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [enabled, siteId, reloadKey, retryKey]);

  // After selection, bring the action panel into view (mobile: it used to sit below the fold).
  useEffect(() => {
    if (!selected) return;
    const id = window.requestAnimationFrame(() => {
      const el = actionsRef.current;
      if (!el || typeof el.scrollIntoView !== 'function') return;
      el.scrollIntoView({
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });
    });
    return () => window.cancelAnimationFrame(id);
  }, [selected?.id]);

  const visible = useMemo(
    () => filterFacilitySpiritContainers(containers, { showEmpty }),
    [containers, showEmpty]
  );
  const groups = useMemo(() => groupSpiritContainersByKind(visible), [visible]);
  const emptyCount = useMemo(
    () => containers.filter((c) => !filterFacilitySpiritContainers([c], { showEmpty: false }).length).length,
    [containers]
  );

  if (!enabled) return null;

  const openAction = (mode: 'bottle' | 'transfer' | 'history', container: FacilitySpiritContainer) => {
    navigate(
      spiritsDeepLink({
        siteId,
        mode,
        containerId: container.id,
      })
    );
    setSelected(null);
  };

  const renderActions = (container: FacilitySpiritContainer) => {
    const destroyed = isSpiritContainerDestroyed(container);
    return (
      <div
        ref={actionsRef}
        className="facility-spirit-actions facility-spirit-actions--inline"
        data-testid="facility-spirit-actions"
        role="region"
        aria-label={`Actions for ${container.containerSerial}`}
      >
        <div className="facility-spirit-actions__summary">
          <strong>{container.containerSerial}</strong>
          <span>
            {container.kind || 'Spirit'} @ {container.proof != null ? `${Number(container.proof)}°` : '—'} ·{' '}
            {formatSpiritProofGallons(container.remainingProofGallons)} PG
            {destroyed ? ' · Destroyed (not reusable)' : ''}
          </span>
        </div>
        <div className="facility-spirit-actions__buttons">
          {!destroyed && (
            <>
              <button
                type="button"
                className="btn btn-primary"
                data-testid="facility-spirit-action-bottle"
                onClick={() => openAction('bottle', container)}
              >
                Bottle
              </button>
              <button
                type="button"
                className="btn btn-outline-primary"
                data-testid="facility-spirit-action-transfer"
                onClick={() => openAction('transfer', container)}
              >
                Transfer
              </button>
            </>
          )}
          <button
            type="button"
            className="btn btn-outline-secondary"
            data-testid="facility-spirit-action-history"
            onClick={() => openAction('history', container)}
          >
            History
          </button>
          <button
            type="button"
            className="btn btn-link"
            data-testid="facility-spirit-action-close"
            onClick={() => setSelected(null)}
          >
            Close
          </button>
        </div>
      </div>
    );
  };

  return (
    <section className="facility-spirits" data-testid="facility-spirits-panel" aria-label="Spirits containers">
      <header className="facility-spirits__header">
        <div>
          <h3 className="facility-spirits__title">Spirits containers</h3>
          <p className="facility-spirits__subtitle">
            Drums and barrels on hand at this distillery (not brewery tanks).
          </p>
        </div>
        <label className="facility-spirits__toggle">
          <input
            type="checkbox"
            checked={showEmpty}
            onChange={(e) => setShowEmpty(e.target.checked)}
            data-testid="facility-spirits-show-empty"
          />
          Show empty{emptyCount ? ` (${emptyCount})` : ''}
        </label>
      </header>

      {error && (
        <div className="facility-spirits__error" data-testid="facility-spirits-error" role="alert">
          {error}{' '}
          <button
            type="button"
            className="facility-spirits__retry"
            onClick={() => setRetryKey((k) => k + 1)}
            data-testid="facility-spirits-retry"
          >
            Retry
          </button>
        </div>
      )}
      {loading && (
        <p className="facility-spirits__muted" data-testid="facility-spirits-loading">
          Loading containers…
        </p>
      )}
      {!loading && !error && groups.length === 0 && (
        <p className="facility-spirits__muted" data-testid="facility-spirits-empty">
          {containers.length === 0
            ? 'No spirits containers at this distillery yet. Receive drums or barrels from the Spirits menu.'
            : 'No non-empty containers. Turn on “Show empty” to see drained drums/barrels.'}
        </p>
      )}

      {groups.map((group) => (
        <div key={group.kind} className="facility-spirits__group" data-testid={`facility-spirits-kind-${group.kind}`}>
          <h4 className="facility-spirits__kind">{group.kind}</h4>
          <ul className="facility-spirits__grid">
            {group.containers.map((c) => {
              const fill = spiritContainerFillPercent(c);
              const pg = formatSpiritProofGallons(c.remainingProofGallons);
              const empty = filterFacilitySpiritContainers([c], { showEmpty: false }).length === 0;
              const isSelected = selected?.id === c.id;
              return (
                <li
                  key={c.id}
                  className={`facility-spirit-tile-item${isSelected ? ' facility-spirit-tile-item--selected' : ''}`}
                >
                  <button
                    type="button"
                    className={`facility-spirit-tile${empty ? ' facility-spirit-tile--empty' : ''}${
                      isSelected ? ' facility-spirit-tile--selected' : ''
                    }`}
                    data-testid={`facility-spirit-tile-${c.id}`}
                    aria-pressed={isSelected}
                    aria-expanded={isSelected}
                    onClick={() => setSelected(isSelected ? null : c)}
                  >
                    <div className="facility-spirit-tile__serial">{c.containerSerial}</div>
                    <div className="facility-spirit-tile__kind">
                      {c.kind || 'Spirit'}
                      {c.containerType ? ` · ${c.containerType}` : ''}
                      {empty ? ' · Destroyed' : ''}
                    </div>
                    <div className="facility-spirit-tile__meta">
                      {c.proof != null ? `${Number(c.proof)}°` : '—'} · {pg} PG
                    </div>
                    <div
                      className="facility-spirit-tile__bar"
                      role="progressbar"
                      aria-valuenow={Math.round(fill)}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-label="Proof gallons remaining"
                    >
                      <span className="facility-spirit-tile__bar-fill" style={{ width: `${fill}%` }} />
                    </div>
                  </button>
                  {/* Inline under the tapped tile so phone users see actions without hunting below the list. */}
                  {isSelected ? renderActions(c) : null}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </section>
  );
};

export default FacilitySpiritsPanel;
